import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Shield, Mic, Camera, Loader2, Trash2, X, Check, BadgeCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';

/**
 * GreenIAPanel — Full-screen modal for anti-deepfake protection.
 * Uses LIVE camera (getUserMedia + <video> + <canvas>) and LIVE mic (MediaRecorder).
 * NO file uploads: identity verification MUST come from the user's own device in real time.
 * Props:
 *   - onClose(): function to close the modal
 */
export default function GreenIAPanel({ onClose }) {
  const { language } = useLanguage();
  const T = myiaTexts(language);

  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);

  // Live photo capture
  const [cameraOn, setCameraOn] = useState(false);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Live voice recording
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const audioStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [form, setForm] = useState({ first_name: '', last_name: '', age: '' });
  const [saving, setSaving] = useState(false);

  // Fetch existing registration
  useEffect(() => {
    axios.get('/api/green-ia/mine')
      .then(r => setExisting(r.data?.data || null))
      .finally(() => setLoading(false));
  }, []);

  // Cleanup: stop all streams when unmounting
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (audioStreamRef.current) audioStreamRef.current.getTracks().forEach(t => t.stop());
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  // --- LIVE CAMERA ---
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
      // Wait for React to render the <video> before assigning
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      toast.error(T.giCameraKo);
    }
  };

  const capturePhoto = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const size = Math.min(v.videoWidth, v.videoHeight) || 480;
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    // Center-crop square
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
    c.toBlob(b => {
      if (!b) return;
      setPhotoBlob(b);
      setPhotoUrl(URL.createObjectURL(b));
      // Stop camera stream after capture
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      setCameraOn(false);
    }, 'image/jpeg', 0.9);
  };

  const retakePhoto = () => {
    setPhotoBlob(null);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(null);
    openCamera();
  };

  // --- LIVE VOICE ---
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: 'audio/webm' });
        setVoiceBlob(b);
        setVoiceUrl(URL.createObjectURL(b));
        stream.getTracks().forEach(t => t.stop());
        audioStreamRef.current = null;
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch {
      toast.error(T.giMicKo);
    }
  };

  const stopRec = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const removeVoice = () => {
    setVoiceBlob(null);
    if (voiceUrl) URL.revokeObjectURL(voiceUrl);
    setVoiceUrl(null);
  };

  // --- SUBMIT ---
  const submit = async () => {
    if (!photoBlob || !voiceBlob || !form.first_name.trim() || !form.last_name.trim()) {
      toast.error(T.giRequired);
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('photo', photoBlob, 'face.jpg');
      fd.append('voice', voiceBlob, 'voice.webm');
      fd.append('first_name', form.first_name.trim());
      fd.append('last_name', form.last_name.trim());
      if (form.age) fd.append('age', String(form.age));
      const r = await axios.post('/api/green-ia/register', fd);
      const g = await axios.get('/api/green-ia/mine');
      setExisting(g.data?.data || null);
      const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`;
      toast.success(T.giOk.replace('{name}', r.data?.registered_name || fullName));
      // Reset
      setPhotoBlob(null); setPhotoUrl(null);
      setVoiceBlob(null); setVoiceUrl(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.giKo);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(T.giRemoveConfirm)) return;
    try {
      await axios.delete('/api/green-ia/mine');
      setExisting(null);
      toast.success(T.giRemoved);
    } catch {
      toast.error(T.giKo);
    }
  };

  const displayName = `${form.first_name || ''} ${form.last_name || ''}`.trim() || '…';

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md overflow-y-auto" data-testid="green-ia-modal">
      <div className="min-h-full flex items-start justify-center px-4 py-6">
        <div className="w-full max-w-lg bg-gradient-to-br from-green-950/95 to-emerald-950/95 border border-green-500/40 rounded-3xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-green-500/20 sticky top-0 bg-green-950/95 backdrop-blur-md rounded-t-3xl">
            <div className="flex items-center gap-3">
              {/* Green IA logo SVG (transparent bg) */}
              <svg width="36" height="36" viewBox="0 0 40 40" fill="none" data-testid="green-ia-logo">
                <defs>
                  <linearGradient id="gia-modal-g" x1="0" y1="0" x2="40" y2="40">
                    <stop offset="0" stopColor="#22c55e" />
                    <stop offset="1" stopColor="#065f46" />
                  </linearGradient>
                </defs>
                <path d="M20 3 L34 9 V22 C34 30 27 36 20 38 C13 36 6 30 6 22 V9 Z" fill="url(#gia-modal-g)" stroke="#a7f3d0" strokeWidth="1"/>
                <path d="M20 12 C15 15 15 22 20 26 C25 22 25 15 20 12 Z" fill="#a7f3d0"/>
                <circle cx="20" cy="18" r="2" fill="#065f46"/>
              </svg>
              <div>
                <h3 className="font-bold text-green-100 text-base flex items-center gap-1">
                  {T.giModalTitle}
                  <BadgeCheck className="w-4 h-4 text-green-300" />
                </h3>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="green-ia-close">
              <X className="w-5 h-5 text-green-100" />
            </button>
          </div>

          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-green-300" /></div>
          ) : existing ? (
            /* --- Already protected --- */
            <div className="p-5 space-y-3">
              <div className="p-4 rounded-2xl bg-black/40 border border-green-500/30">
                <div className="flex items-center gap-3">
                  {existing.photo_thumb_b64 && (
                    <img src={`data:image/jpeg;base64,${existing.photo_thumb_b64}`} alt="me" className="w-16 h-16 rounded-2xl object-cover border border-green-500/40" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-green-100 font-bold">
                      <Shield className="w-4 h-4 text-green-300" />
                      {T.giExistingTitle}
                    </div>
                    <div className="text-xs text-green-200 mt-0.5">{existing.first_name} {existing.last_name}</div>
                    <div className="text-[10px] text-green-200/60 mt-0.5">
                      {T.giExistingSub.replace('{id}', existing.id?.slice(0, 8) + '…')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-[11px] text-green-100 leading-snug">
                ✓ {T.giHashNote}
              </div>
              <button onClick={remove} className="w-full py-3 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-200 font-semibold text-sm flex items-center justify-center gap-2" data-testid="green-ia-remove">
                <Trash2 className="w-4 h-4" /> {T.giRemove}
              </button>
            </div>
          ) : (
            /* --- Register --- */
            <div className="p-5 space-y-4">
              {/* Intro */}
              <p className="text-[13px] text-green-100/90 leading-relaxed">{T.giModalIntro}</p>

              {/* Origin story — detailed reason for Green IA's creation (v50.15) */}
              {T.giStory && (
                <div className="p-3 rounded-xl bg-black/30 border border-green-500/15" data-testid="green-ia-story">
                  <p className="text-[11px] text-green-100/75 leading-relaxed">{T.giStory}</p>
                </div>
              )}

              {/* Why bullets */}
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                <div className="text-xs font-bold text-green-200 mb-1.5">{T.giWhyTitle}</div>
                <ul className="space-y-1 text-[11px] text-green-100/85">
                  <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-green-300 mt-0.5 shrink-0" />{T.giWhyBullet1}</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-green-300 mt-0.5 shrink-0" />{T.giWhyBullet2}</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-green-300 mt-0.5 shrink-0" />{T.giWhyBullet3}</li>
                </ul>
              </div>

              {/* Identity fields */}
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })}
                    placeholder={T.giFirstName}
                    className="bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-green-50 placeholder-green-300/40 focus:outline-none focus:border-green-400"
                    data-testid="green-ia-first-name"
                  />
                  <input
                    value={form.last_name}
                    onChange={e => setForm({ ...form, last_name: e.target.value })}
                    placeholder={T.giLastName}
                    className="bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-green-50 placeholder-green-300/40 focus:outline-none focus:border-green-400"
                    data-testid="green-ia-last-name"
                  />
                </div>
                <input
                  type="number"
                  min="1" max="120"
                  value={form.age}
                  onChange={e => setForm({ ...form, age: e.target.value })}
                  placeholder={T.giAge}
                  className="w-full bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-green-50 placeholder-green-300/40 focus:outline-none focus:border-green-400"
                  data-testid="green-ia-age"
                />
              </div>

              {/* STEP 1 — LIVE PHOTO */}
              <div className="p-3 rounded-2xl bg-black/40 border border-green-500/20">
                <div className="text-xs font-bold text-green-200 mb-2 flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5" /> {T.giPhotoStep}
                </div>
                {photoUrl ? (
                  <div className="space-y-2">
                    <img src={photoUrl} alt="captured" className="w-full aspect-square rounded-xl object-cover border border-green-500/40" data-testid="green-ia-photo-preview" />
                    <button onClick={retakePhoto} className="w-full py-2 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center justify-center gap-2" data-testid="green-ia-retake">
                      <RefreshCw className="w-3.5 h-3.5" /> {T.giRetake}
                    </button>
                  </div>
                ) : cameraOn ? (
                  <div className="space-y-2">
                    <div className="relative aspect-square rounded-xl overflow-hidden bg-black border border-green-500/40">
                      <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="green-ia-live-video" />
                      <div className="absolute inset-4 border-2 border-green-400/60 rounded-full pointer-events-none" />
                    </div>
                    <button onClick={capturePhoto} className="w-full py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:brightness-110 font-bold text-sm flex items-center justify-center gap-2" data-testid="green-ia-capture">
                      <Camera className="w-4 h-4" /> {T.giCapture}
                    </button>
                  </div>
                ) : (
                  <button onClick={openCamera} className="w-full py-3 rounded-full bg-black/50 hover:bg-black/70 border border-green-500/40 text-sm font-semibold flex items-center justify-center gap-2 text-green-100" data-testid="green-ia-open-camera">
                    <Camera className="w-4 h-4" /> {T.giOpenCamera}
                  </button>
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              {/* STEP 2 — LIVE VOICE */}
              <div className="p-3 rounded-2xl bg-black/40 border border-green-500/20">
                <div className="text-xs font-bold text-green-200 mb-2 flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5" /> {T.giVoiceStep}
                </div>
                <p className="text-[10px] text-green-100/60 italic mb-2 leading-snug">
                  {T.giVoiceHint.replace('{name}', displayName)}
                </p>
                {!voiceUrl ? (
                  <button
                    onClick={recording ? stopRec : startRec}
                    className={`w-full py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2 ${recording ? 'bg-red-500 animate-pulse text-white' : 'bg-black/50 hover:bg-black/70 border border-green-500/40 text-green-100'}`}
                    data-testid="green-ia-rec"
                  >
                    <Mic className="w-4 h-4" />
                    {recording ? `${T.giVoiceStop} (${seconds}s)` : T.giVoiceStart}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <audio src={voiceUrl} controls className="flex-1 h-8" data-testid="green-ia-voice-preview" />
                    <button onClick={removeVoice} className="p-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="green-ia-voice-remove">
                      <X className="w-4 h-4 text-green-100" />
                    </button>
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={submit}
                disabled={saving || !photoBlob || !voiceBlob || !form.first_name.trim() || !form.last_name.trim()}
                className="w-full py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:brightness-110 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
                data-testid="green-ia-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                {saving ? T.giSubmitting : T.giSubmit}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
